import {
  tsvClipboardPayloadSerializer,
  tsvClipboardSourceAdapter,
} from 'helpers/tableClipboard.helper/tableClipboard.helper'
import type { ClipboardPayload } from 'helpers/tableClipboard.helper/tableClipboard.helper.types'
import type { CellValue, ColumnGroup } from './Table.utils'
import {
  CELL_STATE_CLASSES,
  cellIdToPosition,
  GROUP_DIVIDER_CLASS,
  getCellId,
  getCellState,
  getColumnGroupSpans,
  getRectangularCellSelection,
  getSpecialKey,
  isShadedColumn,
  SHADED_COLUMN_CLASS,
} from './Table.utils'
import { TableGroupHeaders } from './TableGroupHeaders'

export type { CellValue, ColumnGroup } from './Table.utils'
export {
  cellIdToPosition,
  getCellId,
  getRectangularCellSelection,
  getSpecialKey,
} from './Table.utils'

import { useVirtualizer } from '@tanstack/react-virtual'
import clsx from 'clsx'
import { clone } from 'helpers/global.helper/global.helper'
import { type ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react'

export function getLookUpTables(
  hiddenColumnIndicesSet: Set<number>,
  totalDataColumns: number
): {
  tableColumnToDataColumn: Record<number, number>
  dataColumnToTableColumn: Record<number, number>
} {
  const tableColumnToDataColumn: Record<number, number> = {}
  const dataColumnToTableColumn: Record<number, number> = {}

  let tableIndex = 0

  for (let dataIndex = 0; dataIndex < totalDataColumns; dataIndex++) {
    if (hiddenColumnIndicesSet.has(dataIndex)) {
      continue
    }

    tableColumnToDataColumn[tableIndex] = dataIndex
    dataColumnToTableColumn[dataIndex] = tableIndex
    tableIndex++
  }

  return { tableColumnToDataColumn, dataColumnToTableColumn }
}

function cellValueToNumberOrNull(value: number | CellValue): number | null {
  if (value === null || value === '') {
    return null
  }

  if (typeof value === 'number') {
    if (isNaN(value)) {
      return null
    }

    return value
  }

  if (typeof value === 'string') {
    const parsedValue = parseFloat(value.replace(',', '.'))

    if (isNaN(parsedValue)) {
      return null
    }

    return parsedValue
  }

  return null
}

export function isValidCellValue(value: CellValue): boolean {
  if (value === null) {
    return true
  }

  return /^[-]?\d*[,.]?\d*$/.test(value)
}

export function formatCellValue(value: CellValue): CellValue {
  if (value === null || value === '' || value === '-' || value === undefined) {
    return null
  }

  // Copy value to a new variable to avoid modifying the original
  let newValue = `${value}`

  // Add leading zero if value starts with . or ,
  if (newValue.startsWith('.') || newValue.startsWith(',')) {
    newValue = `0${newValue}`
  }

  // Add trailing zero if value ends with . or ,
  if (newValue.endsWith('.') || newValue.endsWith(',')) {
    newValue = `${newValue}0`
  }

  return newValue.replace(',', '.')
}

type TableClipboardSelectionInput<T> = {
  tableData: T[][]
  cells: Set<string>
  dataColumnToTableColumn: Record<number, number>
}

interface TableClipboardSelectionSourceAdapter {
  toClipboardPayload: <T>(input: TableClipboardSelectionInput<T>) => ClipboardPayload
}

export const tableClipboardSelectionSourceAdapter: TableClipboardSelectionSourceAdapter = {
  toClipboardPayload<T>({
    tableData,
    cells,
    dataColumnToTableColumn,
  }: TableClipboardSelectionInput<T>): ClipboardPayload {
    const cellPositions = Array.from(cells).map((c) => {
      const pos = cellIdToPosition(c)
      const tableColumnIndex = dataColumnToTableColumn[pos.columnIndex]

      return {
        rowIndex: pos.rowIndex,
        tableColumnIndex,
      }
    })
    const validCellPositions = cellPositions.filter((pos) => pos.tableColumnIndex !== undefined)

    if (validCellPositions.length === 0) {
      return []
    }

    const minRow = Math.min(...validCellPositions.map((pos) => pos.rowIndex))
    const maxRow = Math.max(...validCellPositions.map((pos) => pos.rowIndex))
    const minColumn = Math.min(...validCellPositions.map((pos) => pos.tableColumnIndex))
    const maxColumn = Math.max(...validCellPositions.map((pos) => pos.tableColumnIndex))

    const selectedCellValueByCoordinate = new Map<string, string>()
    validCellPositions.forEach(({ rowIndex, tableColumnIndex }) => {
      selectedCellValueByCoordinate.set(
        `${rowIndex}:${tableColumnIndex}`,
        String(tableData[rowIndex]?.[tableColumnIndex] ?? '')
      )
    })

    const payload: string[][] = []
    for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex++) {
      const row: string[] = []

      for (let columnIndex = minColumn; columnIndex <= maxColumn; columnIndex++) {
        row.push(selectedCellValueByCoordinate.get(`${rowIndex}:${columnIndex}`) ?? '')
      }

      payload.push(row)
    }

    return payload
  },
}

function copyCellValuesToClipboard<T>(
  tableData: T[][],
  cells: Set<string>,
  dataColumnToTableColumn: Record<number, number>
): void {
  const payload = tableClipboardSelectionSourceAdapter.toClipboardPayload({
    tableData,
    cells,
    dataColumnToTableColumn,
  })

  navigator.clipboard.writeText(tsvClipboardPayloadSerializer.fromClipboardPayload(payload))
}

function readChangesFromClipboardText(
  currentData: CellValue[][],
  originCell: string,
  clipboardText: string,
  tableColumnToDataColumn: Record<number, number>,
  dataColumnToTableColumn: Record<number, number>
): Record<string, CellValue> {
  const originPosition = cellIdToPosition(originCell)
  const originRowIndex = originPosition.rowIndex
  const originalDataColumnIndex = originPosition.columnIndex
  const originalTableColumnIndex = dataColumnToTableColumn[originalDataColumnIndex]
  const changes: Record<string, CellValue> = {}
  const maxRows = currentData.length - 1
  const maxColumns = (currentData[0]?.length || 1) - 1

  tsvClipboardSourceAdapter.toClipboardPayload(clipboardText).forEach((row, rowIndex) => {
    row.forEach((value, columnIndex) => {
      const cellRowIndex = originRowIndex + rowIndex
      const cellTableColumnIndex = originalTableColumnIndex + columnIndex

      // Out of bounds check
      if (cellRowIndex > maxRows || cellTableColumnIndex > maxColumns) {
        return
      }

      const dataColumnIndex = tableColumnToDataColumn[cellTableColumnIndex]
      const cellId = getCellId(cellRowIndex, dataColumnIndex)

      if (value === '') {
        // Don't apply empty changes, keep the original value
      } else if (isValidCellValue(value)) {
        changes[cellId] = formatCellValue(value)
      } else {
        changes[cellId] = null
      }
    })
  })

  return changes
}

function eventIsInsideTableElement(ev: KeyboardEvent): boolean {
  const target = ev.target as HTMLElement
  const tableElement = document.querySelector('[data-table-root-id="table"]')

  if (!tableElement) {
    return false
  }

  return tableElement.contains(target)
}

export function isEqualPendingChanges(changes1: PendingChanges, changes2: PendingChanges): boolean {
  if (Object.keys(changes1).length !== Object.keys(changes2).length) {
    return false
  }

  for (const key in changes1) {
    const value1 = changes1[key]
    const value2 = changes2[key]

    if (value1 !== value2) {
      return false
    }
  }

  return true
}

type PendingChanges = {
  [key: string]: CellValue
}

type DragPosition = {
  rowIndex: number
  tableColumnIndex: number
}

type CellPositionAndValue = {
  rowIndex: number
  columnIndex: number
  value: CellValue
}

type TableProps = {
  value: CellValue[][]
  pendingCellValues?: CellPositionAndValue[]
  hiddenColumnIndices?: number[]
  readOnly?: boolean
  columnWidth?: number
  firstColumnWidth?: number
  rowHeight?: number
  firstRowHeight?: number
  /**
   * Groups for an extra header row above the column headers, indexed like the header row (index 0 is the
   * time column). Adjacent visible columns with the same group key share one spanning cell; the row is
   * omitted when this is.
   */
  columnGroups?: readonly (ColumnGroup | null)[]
  groupRowHeight?: number
  maxTableHeight?: number
  CellComponent?: ComponentType<{
    columnIndex: number
    rowIndex: number
    value: CellValue
    isSelected?: boolean
    isEdited?: boolean
    isEmpty?: boolean
    isActive?: boolean
  }>
  HeaderComponent?: ComponentType<{
    columnIndex: number
    value: CellValue
  }>
  onSubmit: (
    data: {
      columnIndex: number
      rowIndex: number
      value: number | null
    }[]
  ) => void
  onChange?: (value: CellValue[][]) => void
  onPendingChange?: (pendingChanges: PendingChanges) => void
  onCellSelection?: (selectedCells: { columnIndex: number; rowIndex: number }[]) => void
}

let isDragging = false

export default function Table({
  value: tableValue,
  pendingCellValues = [],
  hiddenColumnIndices = [],
  readOnly = false,
  columnWidth = 100,
  firstColumnWidth = 200,
  rowHeight = 40,
  firstRowHeight = 100,
  columnGroups,
  groupRowHeight = 28,
  maxTableHeight = 800,
  CellComponent,
  HeaderComponent,
  onSubmit,
  onChange = (..._) => {},
  onPendingChange = (..._) => {},
  onCellSelection = (..._) => {},
}: TableProps): React.JSX.Element {
  const [scrollLeft, setScrollLeft] = useState(0)
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set())
  const [dragStart, setDragStart] = useState<DragPosition | null>(null)
  const [lastActiveCellId, setLastActiveCellId] = useState<string | null>(null)
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({})
  const pendingChangesRef = useRef<PendingChanges>(pendingChanges)
  pendingChangesRef.current = pendingChanges
  const inputRef = useRef<HTMLInputElement>(null)

  const hiddenColumnIndicesSet = useMemo(() => {
    return new Set(hiddenColumnIndices)
  }, [hiddenColumnIndices])
  const { tableColumnToDataColumn, dataColumnToTableColumn } = useMemo(() => {
    return getLookUpTables(hiddenColumnIndicesSet, tableValue[0]?.length || 0)
  }, [hiddenColumnIndicesSet, tableValue[0]?.length])
  // conventions-allow: manual-memo — the React Compiler skips Table (a react-hooks lint suppression and useVirtualizer), and `renderDataCells` depends on `lastColumnOfGroupIndices`
  const { groupSpans, lastColumnOfGroupIndices } = useMemo(() => {
    const spans = columnGroups ? getColumnGroupSpans(columnGroups, tableColumnToDataColumn) : []
    return {
      groupSpans: spans,
      lastColumnOfGroupIndices: new Set(spans.map((span) => span.startIndex + span.columnCount - 1)),
    }
  }, [columnGroups, tableColumnToDataColumn])
  const columnHeadersTop = columnGroups ? groupRowHeight : 0
  const headerHeight = columnHeadersTop + firstRowHeight

  const applyPendingChanges = useCallback(
    ({ type, changes }: { type: 'set' | 'override' | 'reset'; changes?: PendingChanges }) => {
      if (!changes) {
        changes = {}
      }

      if (type === 'set') {
        setPendingChanges((prev) => {
          const newChanges = { ...prev, ...changes }

          pendingChangesRef.current = newChanges
          onPendingChange(newChanges)

          return newChanges
        })
      } else if (type === 'override') {
        if (isEqualPendingChanges(changes, pendingChangesRef.current)) {
          return
        }

        pendingChangesRef.current = changes
        setPendingChanges(changes)
        onPendingChange(changes)
      } else if (type === 'reset' && Object.keys(pendingChangesRef.current).length > 0) {
        pendingChangesRef.current = {}
        setPendingChanges({})
        onPendingChange({})
      }
    },
    [onPendingChange]
  )
  const handleCellChange = useCallback(
    (rowIndex: number, dataColumnIndex: number, value: CellValue) => {
      const cellId = getCellId(rowIndex, dataColumnIndex)

      const newValue = value === '' || value === null ? null : value.replace(',', '.')

      const newChanges: PendingChanges = {
        [cellId]: newValue,
      }
      selectedCells.forEach((cellId) => {
        newChanges[cellId] = newValue
      })

      // Batch update all changes at once
      applyPendingChanges({ type: 'set', changes: newChanges })
    },
    [applyPendingChanges, selectedCells]
  )
  const handleSelectColumn = useCallback(
    (ev: React.MouseEvent, dataColumnIndex: number) => {
      if (readOnly) {
        return
      }

      const isCtrlPressed = ev.ctrlKey || ev.metaKey
      const newSelection = new Set<string>()

      // Add all cells in this column (more efficient batch operation)
      for (let r = 1; r < tableValue.length; r++) {
        const cellId = getCellId(r, dataColumnIndex)
        newSelection.add(cellId)
      }

      if (isCtrlPressed) {
        setSelectedCells((prev) => {
          const updatedSelection = new Set(prev)
          newSelection.forEach((cellId) => {
            updatedSelection.add(cellId)
          })
          return updatedSelection
        })
      } else {
        setSelectedCells(newSelection)
      }

      // Select first row of the column
      setLastActiveCellId(getCellId(1, dataColumnIndex))
    },
    [readOnly, tableValue]
  )
  const handleSelectRow = useCallback(
    (ev: React.MouseEvent, rowIndex: number) => {
      if (readOnly) {
        return
      }

      const isCtrlPressed = ev.ctrlKey || ev.metaKey
      const newSelection = new Set<string>()

      // Add all cells in this row (more efficient batch operation)
      const columnCount = tableValue[0]?.length || 0
      for (let dataColumnIndex = 1; dataColumnIndex < columnCount; dataColumnIndex++) {
        if (hiddenColumnIndicesSet.has(dataColumnIndex)) {
          continue
        }

        const cellId = getCellId(rowIndex, dataColumnIndex)
        newSelection.add(cellId)
      }

      if (isCtrlPressed) {
        setSelectedCells((prev) => {
          const updatedSelection = new Set(prev)
          newSelection.forEach((cellId) => {
            updatedSelection.add(cellId)
          })
          return updatedSelection
        })
      } else {
        setSelectedCells(newSelection)
      }

      // Select first column of the row
      setLastActiveCellId(getCellId(rowIndex, 1))
    },
    [readOnly, tableValue, hiddenColumnIndicesSet]
  )

  const { currentData, unfilteredCurrentData } = useMemo(() => {
    let currentData = clone(tableValue)
    let unfilteredCurrentData: CellValue[][]

    // Remove hidden columns
    const hasHiddenColumns = hiddenColumnIndicesSet.size > 0
    if (hasHiddenColumns) {
      unfilteredCurrentData = clone(currentData)

      currentData = currentData.map((row) => row.filter((_, index) => !hiddenColumnIndicesSet.has(index)))
    } else {
      unfilteredCurrentData = currentData
    }

    // Update currentData with pending changes
    Object.entries(pendingChanges).forEach(([key, cellValue]) => {
      const { rowIndex, columnIndex: dataColumnIndex } = cellIdToPosition(key)
      const tableColumnIndex = dataColumnToTableColumn[dataColumnIndex]

      if (!currentData[rowIndex]) {
        currentData[rowIndex] = []
      }

      currentData[rowIndex][tableColumnIndex] = cellValue

      if (hasHiddenColumns) {
        unfilteredCurrentData[rowIndex][dataColumnIndex] = cellValue
        currentData[rowIndex][tableColumnIndex] = cellValue
      } else {
        // Only update the source reference, unfilteredCurrentData is pointed to currentData when we have no hidden columns
        currentData[rowIndex][tableColumnIndex] = cellValue
      }
    })

    return { currentData, unfilteredCurrentData }
  }, [tableValue, pendingChanges, hiddenColumnIndicesSet, dataColumnToTableColumn])

  // Virtualization
  const parentRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  const rowVirtualizer = useVirtualizer({
    count: Math.max(1, currentData.length - 1),
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
  })
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: (currentData[0]?.length ?? 1) - 1, // -1 since first column will be frozen (typically a "time" column)
    getScrollElement: () => parentRef.current,
    estimateSize: () => columnWidth,
    overscan: 2,
  })
  const getCellValue = useCallback(
    (rowIndex: number, tableColumnIndex: number) => {
      return currentData[rowIndex]?.[tableColumnIndex] ?? null
    },
    [currentData]
  )
  const isCellEdited = useCallback(
    (rowIndex: number, dataColumnIndex: number): boolean => {
      return getCellId(rowIndex, dataColumnIndex) in pendingChanges
    },
    [pendingChanges]
  )
  const isCellEmpty = useCallback(
    (rowIndex: number, tableColumnIndex: number): boolean => {
      const cellValue = getCellValue(rowIndex, tableColumnIndex)
      return cellValue === null || cellValue === undefined
    },
    [getCellValue]
  )
  const isCellSelected = useCallback(
    (cellId: string) => {
      return selectedCells.has(cellId)
    },
    [selectedCells]
  )

  // Selection events
  const handleCellMouseDown = useCallback(
    (ev: React.MouseEvent, rowIndex: number, dataColumnIndex: number, tableColumnIndex: number) => {
      if (readOnly) {
        return
      }

      const cellId = getCellId(rowIndex, dataColumnIndex)

      // Check if Ctrl/Cmd key is pressed for multi-select
      const { isCtrlPressed, isShiftPressed } = getSpecialKey(ev)

      if (isShiftPressed && lastActiveCellId) {
        // Select square of cells from last active cell to this one
        const lastPosition = cellIdToPosition(lastActiveCellId)
        const lastDataColNumber = lastPosition.columnIndex
        const lastRowNumber = lastPosition.rowIndex
        const lastColNumber = dataColumnToTableColumn[lastDataColNumber] || 0

        const newSelection = getRectangularCellSelection(
          { rowIndex: lastRowNumber, tableColumnIndex: lastColNumber },
          { rowIndex, tableColumnIndex },
          tableColumnToDataColumn
        )

        // Append new selection to existing selection
        setSelectedCells((prev) => {
          const updatedSelection = new Set(prev)
          newSelection.forEach((cellId) => {
            updatedSelection.add(cellId)
          })
          return updatedSelection
        })

        ev.preventDefault()
      } else if (isCtrlPressed) {
        // Toggle selection for this cell
        setSelectedCells((prev) => {
          const newSelection = new Set(prev)
          if (newSelection.has(cellId)) {
            newSelection.delete(cellId)
          } else {
            newSelection.add(cellId)
          }
          return newSelection
        })
      } else {
        // Start a new selection
        setSelectedCells(new Set([cellId]))
        setDragStart({ rowIndex, tableColumnIndex })
        isDragging = true
      }

      setLastActiveCellId(cellId)
    },
    [dataColumnToTableColumn, lastActiveCellId, readOnly, tableColumnToDataColumn]
  )
  const handleCellMouseOver = useCallback(
    (rowIndex: number, tableColumnIndex: number) => {
      if (!isDragging || !dragStart) {
        return
      }

      const newSelection = getRectangularCellSelection(
        { rowIndex: dragStart.rowIndex, tableColumnIndex: dragStart.tableColumnIndex },
        { rowIndex, tableColumnIndex },
        tableColumnToDataColumn
      )

      setSelectedCells(newSelection)
    },
    [dragStart, tableColumnToDataColumn]
  )
  const handleMouseUp = useCallback(() => {
    isDragging = false
    setDragStart(null)
  }, [])

  // Prop events
  const commitChanges = useCallback(() => {
    const changedData = Object.entries(pendingChanges).map(([key, cellValue]) => {
      const { rowIndex, columnIndex } = cellIdToPosition(key)

      return {
        columnIndex,
        rowIndex,
        value: cellValueToNumberOrNull(cellValue),
      }
    })

    onSubmit(changedData)
  }, [onSubmit, pendingChanges])

  // Helper functions
  const setSelectedToNull = useCallback(() => {
    const newChanges: PendingChanges = {}
    selectedCells.forEach((cellId) => {
      newChanges[cellId] = null
    })

    // Batch update all changes at once
    applyPendingChanges({ type: 'set', changes: newChanges })
  }, [applyPendingChanges, selectedCells])

  // Keyboard events
  const handleKeyDown = useCallback(
    (ev: KeyboardEvent) => {
      const { isCtrlPressed, isSpecialKeyPressed } = getSpecialKey(ev)
      const key = ev.key
      const isInsideTableElement = eventIsInsideTableElement(ev)
      if (!isInsideTableElement) {
        return
      }

      if (readOnly) {
        if (key === 'Escape') {
          setSelectedCells(new Set())
          setLastActiveCellId(null)

          const target = ev.target as HTMLInputElement
          if (target) {
            target.blur()
          }
        }

        return
      }

      // Arrow keys navigation
      if (key === 'ArrowUp' && lastActiveCellId) {
        const { rowIndex, columnIndex } = cellIdToPosition(lastActiveCellId)

        if (rowIndex > 1) {
          const newCellId = getCellId(rowIndex - 1, columnIndex)
          setLastActiveCellId(newCellId)

          if (isSpecialKeyPressed) {
            setSelectedCells((prev) => {
              const newSelection = new Set(prev)
              newSelection.add(newCellId)
              return newSelection
            })
          } else {
            setSelectedCells(new Set([newCellId]))
          }
        }
      } else if ((key === 'ArrowDown' || key === 'Enter') && lastActiveCellId) {
        const { rowIndex, columnIndex } = cellIdToPosition(lastActiveCellId)

        if (rowIndex < tableValue.length - 1) {
          const newCellId = getCellId(rowIndex + 1, columnIndex)
          setLastActiveCellId(newCellId)

          if (isSpecialKeyPressed) {
            setSelectedCells((prev) => {
              const newSelection = new Set(prev)
              newSelection.add(newCellId)
              return newSelection
            })
          } else {
            setSelectedCells(new Set([newCellId]))
          }

          ev.preventDefault()
        }
      } else if (key === 'ArrowLeft' && lastActiveCellId) {
        const { rowIndex, columnIndex } = cellIdToPosition(lastActiveCellId)
        const tableColumnIndex = dataColumnToTableColumn[columnIndex]

        if (tableColumnIndex > 1) {
          const newDataColumnIndex = tableColumnToDataColumn[tableColumnIndex - 1]
          const newCellId = getCellId(rowIndex, newDataColumnIndex)
          setLastActiveCellId(newCellId)

          if (isSpecialKeyPressed) {
            setSelectedCells((prev) => {
              const newSelection = new Set(prev)
              newSelection.add(newCellId)
              return newSelection
            })
          } else {
            setSelectedCells(new Set([newCellId]))
          }
        }
      } else if ((key === 'ArrowRight' || key === 'Tab') && lastActiveCellId) {
        const { rowIndex, columnIndex } = cellIdToPosition(lastActiveCellId)
        const tableColumnIndex = dataColumnToTableColumn[columnIndex]
        const columnCount = currentData[0]?.length || 0
        const maxTableColumnIndex = columnCount - 1

        if (tableColumnIndex < maxTableColumnIndex) {
          const newDataColumnIndex = tableColumnToDataColumn[tableColumnIndex + 1]
          const newCellId = getCellId(rowIndex, newDataColumnIndex)
          setLastActiveCellId(newCellId)

          if (isSpecialKeyPressed) {
            setSelectedCells((prev) => {
              const newSelection = new Set(prev)
              newSelection.add(newCellId)
              return newSelection
            })
          } else {
            setSelectedCells(new Set([newCellId]))
          }

          ev.preventDefault()
        }
      }

      // Save on ctrl + s
      else if (key === 's' && isCtrlPressed) {
        commitChanges()

        setSelectedCells(new Set())
        setLastActiveCellId(null)

        // Blur this input
        const target = ev.target as HTMLInputElement
        if (target) {
          target.blur()
        }

        ev.preventDefault()
        ev.stopPropagation()
      }

      // Select all on ctrl + a
      else if (key === 'a' && isCtrlPressed) {
        const newSelection = new Set<string>()
        for (let r = 1; r < currentData.length; r++) {
          const row = currentData[r]
          for (let c = 1; c < row.length; c++) {
            const dataColumnIndex = tableColumnToDataColumn[c]
            const cellId = getCellId(r, dataColumnIndex)
            newSelection.add(cellId)
          }
        }
        setSelectedCells(newSelection)
      }

      // Copy & paste
      else if (key === 'x' && isCtrlPressed) {
        copyCellValuesToClipboard(currentData, selectedCells, dataColumnToTableColumn)
        setSelectedToNull()
        ev.preventDefault()
        ev.stopPropagation()
      } else if (key === 'c' && isCtrlPressed) {
        copyCellValuesToClipboard(currentData, selectedCells, dataColumnToTableColumn)
        ev.preventDefault()
        ev.stopPropagation()
      }

      // Cancel on escape
      if (key === 'Escape') {
        setSelectedCells(new Set())
        setLastActiveCellId(null)

        // Blur this input
        const target = ev.target as HTMLInputElement
        if (target) {
          target.blur()
        }
      }
    },
    [
      commitChanges,
      currentData,
      dataColumnToTableColumn,
      lastActiveCellId,
      readOnly,
      selectedCells,
      setSelectedToNull,
      tableColumnToDataColumn,
      tableValue,
    ]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])
  useEffect(() => {
    if (!lastActiveCellId) {
      return
    }

    setTimeout(() => {
      if (inputRef?.current?.focus) {
        inputRef.current.focus()
      }
    }, 0)
  }, [lastActiveCellId])

  const ticking = useRef(false)
  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      window.requestAnimationFrame(() => {
        if (parentRef.current) {
          setScrollLeft(parentRef.current.scrollLeft)
        }

        ticking.current = false
      })

      ticking.current = true
    }
  }, [])

  useEffect(() => {
    const changes: PendingChanges = {}

    pendingCellValues.forEach(({ columnIndex, rowIndex, value }) => {
      changes[getCellId(rowIndex, columnIndex)] = value
    })

    applyPendingChanges({
      type: 'override',
      changes,
    })
  }, [applyPendingChanges, pendingCellValues])

  useEffect(() => {
    onChange(unfilteredCurrentData)
  }, [onChange, unfilteredCurrentData])

  useEffect(() => {
    const selectedCellsAsArray = Array.from(selectedCells).map((cellId) => cellIdToPosition(cellId))

    onCellSelection(selectedCellsAsArray)
  }, [onCellSelection, selectedCells])

  // Reset scroll on value change
  useEffect(() => {
    if (!tableValue) {
      return
    }

    setScrollLeft(0)
    setSelectedCells(new Set())
    applyPendingChanges({ type: 'reset' })
  }, [applyPendingChanges, tableValue])

  useEffect(() => {
    if (readOnly) {
      setLastActiveCellId(null)
    }
  }, [readOnly])

  // Read virtual items outside the memo so virtualizer-driven re-renders
  // propagate correctly (the virtualizer triggers re-renders when scroll
  // position or container size changes; the arrays only change identity
  // when the visible range changes).
  const rowVirtualItems = rowVirtualizer.getVirtualItems()
  const colVirtualItems = columnVirtualizer.getVirtualItems()

  // Render the data grid cells (virtualized rows and columns)
  const renderDataCells = useMemo(() => {
    const cells: React.JSX.Element[] = []

    rowVirtualItems.forEach((virtualRow) => {
      const rowIndex = virtualRow.index + 1 // +1 because we skip the header row

      colVirtualItems.forEach((virtualCol) => {
        const tableColumnIndex = virtualCol.index + 1 // +1 to account for time column
        const dataColumnIndex = tableColumnToDataColumn[tableColumnIndex]
        const cellValue = getCellValue(rowIndex, tableColumnIndex)
        const cellId = getCellId(rowIndex, dataColumnIndex)
        const isSelected = isCellSelected(cellId)
        const isEdited = isCellEdited(rowIndex, dataColumnIndex)
        const isEmpty = isCellEmpty(rowIndex, tableColumnIndex)
        const cellState = getCellState({ isSelected, isEmpty, isEdited, readOnly })

        const Cell = CellComponent ? (
          <CellComponent
            value={cellValue}
            rowIndex={rowIndex}
            columnIndex={dataColumnIndex}
            isSelected={isSelected}
            isEdited={isEdited}
            isEmpty={isEmpty}
            isActive={lastActiveCellId === cellId}
          />
        ) : (
          <div
            className={clsx('box-border flex items-center justify-center p-0', CELL_STATE_CLASSES[cellState], {
              italic: isEdited,
            })}
            style={{
              width: `${columnWidth}px`,
              height: `${rowHeight}px`,
            }}
          >
            {readOnly && isEmpty ? '-' : cellValue}
          </div>
        )

        cells.push(
          <div
            key={cellId}
            className={clsx('absolute', columnGroups && isShadedColumn(virtualCol.index) && SHADED_COLUMN_CLASS)}
            style={{
              width: `${columnWidth}px`,
              height: `${rowHeight}px`,
              top: `${virtualRow.start}px`,
              left: `${virtualCol.start + firstColumnWidth}px`,
            }}
            onMouseDown={(ev: React.MouseEvent) => handleCellMouseDown(ev, rowIndex, dataColumnIndex, tableColumnIndex)}
            onMouseOver={() => handleCellMouseOver(rowIndex, tableColumnIndex)}
            onMouseUp={() => handleMouseUp()}
            onFocus={() => handleCellMouseOver(rowIndex, tableColumnIndex)}
          >
            {!readOnly && lastActiveCellId === cellId ? (
              <input
                autoFocus
                ref={inputRef}
                type="text"
                key={cellId}
                aria-label={`Table cell ${rowIndex}-${dataColumnIndex}`}
                className="box-border h-full w-full border-2 border-dashed border-blue-100 bg-blue-400 p-2 text-center outline-none"
                value={cellValue === null ? '' : cellValue}
                onChange={(ev) => {
                  // Only allow numeric input or empty
                  const value = ev.target.value

                  if (!isValidCellValue(value)) {
                    return
                  }

                  handleCellChange(rowIndex, dataColumnIndex, value)
                }}
                onBlur={(ev) => {
                  if (!isValidCellValue(ev.target.value)) {
                    return
                  }

                  ev.stopPropagation()

                  const newChanges: PendingChanges = {}
                  Object.entries(pendingChanges)
                    .map(([key, value]) => {
                      return [key, formatCellValue(value)]
                    })
                    .forEach(([key, value]) => {
                      if (key !== null) {
                        newChanges[key] = value
                      }
                    })

                  if (isEqualPendingChanges(newChanges, pendingChanges)) {
                    return
                  }

                  applyPendingChanges({
                    type: 'override',
                    changes: newChanges,
                  })
                }}
                onPaste={(ev) => {
                  const clipboardText = ev.clipboardData.getData('text').replace(',', '.') // Replace comma with dot for decimal values

                  const newChanges = readChangesFromClipboardText(
                    currentData,
                    cellId,
                    clipboardText,
                    tableColumnToDataColumn,
                    dataColumnToTableColumn
                  )
                  applyPendingChanges({ type: 'set', changes: newChanges })

                  ev.preventDefault()
                  ev.stopPropagation()
                }}
              />
            ) : (
              Cell
            )}
            {columnGroups && lastColumnOfGroupIndices.has(virtualCol.index) && (
              <div className={`${GROUP_DIVIDER_CLASS} top-0 -right-px h-full`} />
            )}
          </div>
        )
      })
    })

    return cells
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applyPendingChanges,
    CellComponent,
    colVirtualItems,
    columnWidth,
    currentData,
    dataColumnToTableColumn,
    firstColumnWidth,
    getCellValue,
    handleCellChange,
    handleCellMouseDown,
    handleCellMouseOver,
    handleMouseUp,
    isCellEdited,
    isCellEmpty,
    isCellSelected,
    lastActiveCellId,
    lastColumnOfGroupIndices,
    columnGroups,
    tableColumnToDataColumn,
    pendingChanges,
    rowVirtualItems,
    rowHeight,
    readOnly,
  ])

  return (
    <>
      {/* Main container */}
      <div
        ref={parentRef}
        data-table-root-id="table"
        className="relative z-10 w-full overflow-auto border-none"
        style={{
          maxHeight: maxTableHeight ? `${maxTableHeight}px` : '',
        }}
        onScroll={handleScroll}
        onMouseUp={handleMouseUp}
      >
        {/* Fixed header row */}
        <div
          className="sticky top-0 right-0 left-0 z-50 w-full"
          style={{
            height: `${headerHeight}px`,
            transform: `translateX(-${scrollLeft}px)`,
          }}
        >
          {/* Time header */}
          {HeaderComponent ? (
            <div
              className="bg-background absolute top-0 left-0 z-40 border-r-2 border-gray-500"
              style={{
                width: `${firstColumnWidth}px`,
                height: `${headerHeight}px`,
                transform: `translateX(${scrollLeft}px)`,
              }}
            >
              <HeaderComponent value={currentData[0]?.[0] ?? null} columnIndex={0} />
            </div>
          ) : (
            <div
              className="bg-background absolute top-0 left-0 z-40 flex items-center justify-center border-r-2 border-b-2 border-gray-500"
              style={{
                width: `${firstColumnWidth}px`,
                height: `${headerHeight}px`,
                transform: `translateX(${scrollLeft}px)`,
              }}
            >
              {currentData[0]?.[0] ?? null}
            </div>
          )}

          <TableGroupHeaders
            spans={groupSpans}
            columnWidth={columnWidth}
            firstColumnWidth={firstColumnWidth}
            rowHeight={groupRowHeight}
            headerHeight={headerHeight}
            scrollLeft={scrollLeft}
          />

          {/* Column headers */}
          {colVirtualItems.map((virtualCol) => {
            const tableColumnIndex = virtualCol.index + 1 // +1 to account for time column
            const dataColumnIndex = tableColumnToDataColumn[tableColumnIndex]
            const headerValue = getCellValue(0, tableColumnIndex)
            const groupDividerClassName = columnGroups ? 'border-r border-gray-200' : undefined

            if (HeaderComponent) {
              return (
                <div
                  key={virtualCol.index}
                  className={clsx(
                    'absolute',
                    columnGroups && isShadedColumn(virtualCol.index) ? SHADED_COLUMN_CLASS : 'bg-background',
                    groupDividerClassName
                  )}
                  style={{
                    width: `${columnWidth}px`,
                    height: `${firstRowHeight}px`,
                    top: `${columnHeadersTop}px`,
                    left: `${virtualCol.start + firstColumnWidth}px`,
                  }}
                  onClick={(ev) => handleSelectColumn(ev, dataColumnIndex)}
                >
                  <HeaderComponent value={headerValue} columnIndex={dataColumnIndex} />
                </div>
              )
            }

            return (
              <div
                key={virtualCol.index}
                className={clsx(
                  'absolute z-30 flex cursor-pointer items-center justify-center overflow-hidden border-b-2 border-gray-500 text-sm break-all',
                  columnGroups && isShadedColumn(virtualCol.index) ? SHADED_COLUMN_CLASS : 'bg-background',
                  groupDividerClassName
                )}
                style={{
                  width: `${columnWidth}px`,
                  height: `${firstRowHeight}px`,
                  top: `${columnHeadersTop}px`,
                  left: `${virtualCol.start + firstColumnWidth}px`,
                }}
                onClick={(ev) => handleSelectColumn(ev, dataColumnIndex)}
              >
                <div className="overflow-hidden p-2 text-center">{headerValue}</div>
              </div>
            )
          })}
        </div>

        {/* Fixed time column */}
        <div
          className="sticky left-0 z-30"
          style={{
            width: `${firstColumnWidth}px`,
          }}
        >
          {rowVirtualItems.map((virtualRow) => {
            const rowIndex = virtualRow.index + 1 // +1 because we skip the header row
            const timeValue = getCellValue(rowIndex, 0) ?? ''

            return (
              <div
                key={virtualRow.index}
                className="bg-background absolute flex cursor-pointer items-center border-r-2 border-gray-500 text-sm"
                style={{
                  width: `${firstColumnWidth}px`,
                  height: `${rowHeight}px`,
                  top: `${virtualRow.start}px`,
                }}
                onClick={(ev) => handleSelectRow(ev, rowIndex)}
              >
                <div className="truncate p-2">{timeValue}</div>
              </div>
            )
          })}
        </div>

        {/* Actual table with data cells */}
        <div
          ref={tableRef}
          className="relative"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: `${columnVirtualizer.getTotalSize() + firstColumnWidth}px`,
          }}
        >
          {/* Virtualized data cells */}
          {renderDataCells}
        </div>
      </div>
    </>
  )
}
